import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { StandWebSocketMessage } from '@pages/stands/types';
import { useDispatch } from 'react-redux';
import { PayloadActionCreator } from '@reduxjs/toolkit';
import { processWebSocketMessageAction } from '@pages/stands/store';

export type WebSocketConnectionPayloads = {
    stands: StandWebSocketMessage;
};

export type WebSocketConnectionFeatureKey = keyof WebSocketConnectionPayloads;

type ConnectionConfig = {
    url: string;
    type: WebSocketConnectionFeatureKey;
};

export type WebSocketStatus = { readyState: number; failed: boolean };

export type WebSocketMessage = object;

type WebSocketContextValue = {
    send: (type: WebSocketConnectionFeatureKey, jsonMsg: object) => void;
    status: Record<WebSocketConnectionFeatureKey, WebSocketStatus>;
};

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

interface MultiWebSocketProviderProps {
    connections: ConnectionConfig[];
    children: ReactNode;
    maxReconnects?: number;
    reconnectInterval?: number;
}

const MultiWebSocketProvider: FC<MultiWebSocketProviderProps> = ({
    connections,
    children,
    maxReconnects = 10,
    reconnectInterval = 3000,
}) => {
    const sendFns = useRef<Partial<Record<WebSocketConnectionFeatureKey, (jsonMsg: WebSocketMessage) => void>>>({});
    const [status, setStatus] = useState<Record<WebSocketConnectionFeatureKey, WebSocketStatus>>({
        stands: { readyState: ReadyState.UNINSTANTIATED, failed: false },
    });

    const registerSend = useCallback(
        (type: WebSocketConnectionFeatureKey, send: (jsonMsg: WebSocketMessage) => void) => {
            sendFns.current[type] = send;
        },
        [],
    );

    const onReadyStateChange = useCallback((type: WebSocketConnectionFeatureKey, status: WebSocketStatus) => {
        setStatus((prev) => ({ ...prev, [type]: status }));
    }, []);

    const send = useCallback((type: WebSocketConnectionFeatureKey, jsonMsg: WebSocketMessage) => {
        sendFns.current[type]?.(jsonMsg);
    }, []);

    return (
        <WebSocketContext.Provider value={{ send, status }}>
            {connections.map((c) => (
                <SocketConnection
                    key={c.type}
                    url={c.url}
                    type={c.type}
                    onReadyStateChange={onReadyStateChange}
                    registerSend={registerSend}
                    maxReconnects={maxReconnects}
                    reconnectInterval={reconnectInterval}
                />
            ))}
            {children}
        </WebSocketContext.Provider>
    );
};

export default MultiWebSocketProvider;

export const useAppWebSockets = () => {
    const ctx = useContext(WebSocketContext);
    if (!ctx) throw new Error('useAppWebSockets must be used inside MultiWebSocketProvider');
    return ctx;
};

const MAP_CONNECTION_KEY_TO_ACTION_CREATOR: {
    [K in WebSocketConnectionFeatureKey]: PayloadActionCreator<WebSocketConnectionPayloads[K]>;
} = {
    stands: processWebSocketMessageAction,
};

type SocketConnectionProps = {
    url: string;
    type: WebSocketConnectionFeatureKey;
    onReadyStateChange: (type: WebSocketConnectionFeatureKey, status: WebSocketStatus) => void;
    registerSend: (type: WebSocketConnectionFeatureKey, send: (msg: WebSocketMessage) => void) => void;
    maxReconnects: number;
    reconnectInterval: number;
};

const SocketConnection: FC<SocketConnectionProps> = ({
    url,
    type,
    onReadyStateChange,
    registerSend,
    maxReconnects,
    reconnectInterval,
}) => {
    const dispatch = useDispatch();
    const [failed, setFailed] = useState(false);

    const handleIncomingMessage = <K extends WebSocketConnectionFeatureKey>(key: K, raw: string) => {
        try {
            const parsed: WebSocketConnectionPayloads[K] = JSON.parse(raw);
            const actionCreator = MAP_CONNECTION_KEY_TO_ACTION_CREATOR[key];
            dispatch(actionCreator(parsed));
        } catch (err) {
            // ignore
        }
    };

    const { sendJsonMessage, readyState } = useWebSocket(url, {
        onMessage: (event) => {
            handleIncomingMessage(type, event.data);
        },
        shouldReconnect: () => true,
        reconnectAttempts: maxReconnects,
        reconnectInterval: reconnectInterval,
        onReconnectStop: () => {
            setFailed(true);
        },
    });

    useEffect(() => {
        registerSend(type, sendJsonMessage);
    }, [type, sendJsonMessage, registerSend]);

    useEffect(() => {
        onReadyStateChange(type, { readyState, failed });
    }, [type, readyState, failed, onReadyStateChange]);

    return null;
};
